import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Copy,
  ExternalLink,
  Link2,
  Link2Off,
  Monitor,
  RotateCw,
  Share2,
  Smartphone,
  Store,
} from 'lucide-react';
import {
  Button,
  EmptyState,
  LoadingState,
  PageHeader,
  Pill,
  PillGroup,
} from '@/components/ui';
import {
  createTestLink,
  getPreviewLink,
  revokeTestLinks,
} from '@/api/directSale';
import { PREVIEW_FRAME_PARAM } from '@/lib/preview-frame';
import { cn } from '@/lib/utils';

type Device = 'phone' | 'desktop';

/**
 * The seller's own look at the configurator their customers get: Direct
 * Sale, embedded the way a shop embeds it, in a phone-sized or a wide frame.
 *
 * The entry link is signed by the API for five minutes and traded for a
 * session by the frame at once; "reload" asks for a fresh one. The frame is
 * marked as a preview (lib/preview-frame.ts), so it keeps its state apart
 * from the seller's Creator and never sees the seller's tokens.
 *
 * There is no shop around this frame: "buy" hands the order to the
 * embedding shop's page, and here nothing listens.
 */
export function ShopPreviewPage() {
  const { t, i18n } = useTranslation();
  const [device, setDevice] = useState<Device>('phone');

  const link = useQuery({
    queryKey: ['direct-sale-preview-link'],
    queryFn: getPreviewLink,
    // A link is good for five minutes and used once; never serve a cached one.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const src =
    link.data?.available && link.data.shop
      ? `/direct-sale?${new URLSearchParams({
          shop: link.data.shop,
          ts: String(link.data.ts),
          sig: link.data.sig ?? '',
          lang: i18n.language,
          [PREVIEW_FRAME_PARAM]: '1',
        }).toString()}`
      : null;

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        eyebrow={t('seller.shopPreview.eyebrow')}
        title={t('seller.shopPreview.title')}
      />
      <p className="max-w-prose text-sm text-mute">
        {t('seller.shopPreview.intro')}
      </p>

      {/* Needs no preview link of its own: the test link is its own token. */}
      <ShareTestLink />

      {link.isPending && <LoadingState />}

      {(link.isError || link.data?.available === false) && (
        <EmptyState
          icon={<Store className="h-6 w-6" aria-hidden="true" />}
          title={t('seller.shopPreview.unavailable.title')}
          description={t('seller.shopPreview.unavailable.description')}
          action={
            <Button variant="secondary" onClick={() => void link.refetch()}>
              {t('seller.shopPreview.reload')}
            </Button>
          }
        />
      )}

      {src && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <PillGroup aria-label={t('seller.shopPreview.deviceLabel')}>
              <Pill
                active={device === 'phone'}
                aria-pressed={device === 'phone'}
                onClick={() => setDevice('phone')}
              >
                <Smartphone className="h-4 w-4" aria-hidden="true" />
                {t('seller.shopPreview.phone')}
              </Pill>
              <Pill
                active={device === 'desktop'}
                aria-pressed={device === 'desktop'}
                onClick={() => setDevice('desktop')}
              >
                <Monitor className="h-4 w-4" aria-hidden="true" />
                {t('seller.shopPreview.desktop')}
              </Pill>
            </PillGroup>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void link.refetch()}
              disabled={link.isFetching}
            >
              <RotateCw
                className={cn('h-4 w-4', link.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
              {t('seller.shopPreview.reload')}
            </Button>
            {link.data?.ownShop === false && (
              <span className="text-xs text-mute">
                {t('seller.shopPreview.demoShop')}
              </span>
            )}
          </div>

          <div className="flex justify-center rounded-card border border-line bg-paper-2 p-4">
            {/* Keyed by the link: a fresh link remounts the frame, which is
                what "reload" means. The permissions are the ones a shop has
                to grant for sharing to work. */}
            <iframe
              key={src}
              src={src}
              title={t('seller.shopPreview.frameTitle')}
              allow="web-share; clipboard-write"
              className={cn(
                'border border-line bg-paper shadow-[0_8px_30px_rgba(13,13,13,0.12)]',
                device === 'phone'
                  ? 'h-[780px] w-[390px] max-w-full rounded-3xl'
                  : 'h-[780px] w-full rounded-card'
              )}
            />
          </div>

          <p className="max-w-prose text-xs text-mute">
            {t('seller.shopPreview.buyNote')}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * A link to the bare configurator — no seller area, no frame — to open on
 * an iPhone or pass to someone else. Valid for 30 days; each click makes a
 * new one, the old ones keep working until they expire — or until the
 * seller withdraws them all, which only a seller with a shop of their own
 * can do (the demo shop is shared).
 */
function ShareTestLink() {
  const { t, i18n } = useTranslation();
  const create = useMutation({ mutationFn: createTestLink });
  const revoke = useMutation({
    mutationFn: revokeTestLinks,
    onSuccess: ({ revoked }) => {
      if (!revoked) return;
      // The link on screen is one of the withdrawn ones.
      create.reset();
      toast.success(t('seller.shopPreview.testLink.revoked'));
    },
    onError: () => toast.error(t('seller.shopPreview.testLink.revokeFailed')),
  });
  const ownShop = create.data?.ownShop === true;

  const token = create.data?.available ? create.data.token : undefined;
  const url = token
    ? `${window.location.origin}/try/${token}?${new URLSearchParams({
        lang: i18n.language,
      }).toString()}`
    : null;
  const expires = create.data?.expiresAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
        new Date(create.data.expiresAt * 1000)
      )
    : null;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('seller.shopPreview.testLink.copied'));
    } catch {
      toast.error(t('seller.shopPreview.testLink.copyFailed'));
    }
  };

  const share = () => {
    if (!url) return;
    // A cancelled share sheet rejects too; nothing to report then.
    void navigator
      .share({ title: t('seller.shopPreview.title'), url })
      .catch(() => undefined);
  };

  return (
    <section className="flex max-w-2xl flex-col gap-3 rounded-card border border-line bg-paper p-4">
      <div className="flex items-start gap-3">
        <Link2
          className="mt-0.5 h-5 w-5 flex-none text-primary"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {t('seller.shopPreview.testLink.title')}
          </h2>
          <p className="text-sm text-mute">
            {t('seller.shopPreview.testLink.description')}
          </p>
        </div>
      </div>

      {url ? (
        <>
          <input
            readOnly
            value={url}
            aria-label={t('seller.shopPreview.testLink.urlLabel')}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full truncate rounded-control border border-line bg-paper-2 px-3 py-2 font-mono text-xs text-ink"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => void copy()}>
              <Copy className="h-4 w-4" aria-hidden="true" />
              {t('seller.shopPreview.testLink.copy')}
            </Button>
            {typeof navigator.share === 'function' && (
              <Button variant="secondary" size="sm" onClick={share}>
                <Share2 className="h-4 w-4" aria-hidden="true" />
                {t('seller.shopPreview.testLink.share')}
              </Button>
            )}
            <Button variant="ghost" size="sm" asChild>
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                {t('seller.shopPreview.testLink.open')}
              </a>
            </Button>
            {expires && (
              <span className="text-xs text-mute">
                {t('seller.shopPreview.testLink.expires', { date: expires })}
              </span>
            )}
          </div>
          {create.data?.ownShop === false && (
            <p className="text-xs text-mute">
              {t('seller.shopPreview.testLink.demoShop')}
            </p>
          )}
          {ownShop && (
            <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
              >
                <Link2Off className="h-4 w-4" aria-hidden="true" />
                {t('seller.shopPreview.testLink.revoke')}
              </Button>
              <span className="text-xs text-mute">
                {t('seller.shopPreview.testLink.revokeHint')}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            {t('seller.shopPreview.testLink.create')}
          </Button>
          {(create.isError || create.data?.available === false) && (
            <span role="alert" className="text-xs text-danger">
              {t('seller.shopPreview.testLink.failed')}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
